/**
 * Сменные задания прорабам (ПЛ-28): из обязательств недели — работа на день смены с объёмом на день.
 * Объём подсказывается равномерно по рабочим дням календаря или по ресурсной кривой, его можно поправить.
 * Прораб видит задание в «Моём дне» и сдаёт по нему отчёт смены; принятый отчёт меняет факт графика.
 */
import {
  type CommitmentRow,
  SHIFT_METHODS,
  type ShiftQuantityHint,
  type ShiftTaskList,
  type ShiftTaskRow,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from './LField';

const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');
const shift = (d: string, k: number) => {
  const t = new Date(`${d}T00:00:00Z`);
  t.setUTCDate(t.getUTCDate() + k);
  return t.toISOString().slice(0, 10);
};
const num = (s: string) => s.replace('.', ',');

export function ShiftTasksBlock({
  scheduleId,
  projectId,
  week,
  commitments,
  people,
}: {
  scheduleId: string;
  projectId: string;
  week: string;
  commitments: CommitmentRow[];
  people: Array<{ id: string; name: string }>;
}) {
  const qc = useQueryClient();
  const to = shift(week, 6);
  const q = useQuery({
    queryKey: ['shift-tasks', scheduleId, week],
    queryFn: () => api<ShiftTaskList>(`/schedules/${scheduleId}/shift-tasks`, { query: { from: week, to } }),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['shift-tasks', scheduleId] });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/shift-tasks/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast('Задание отменено');
      refresh();
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });
  const d = q.data;
  const byDay = useMemo(() => {
    const m = new Map<string, ShiftTaskRow[]>();
    for (const t of d?.tasks ?? []) m.set(t.day, [...(m.get(t.day) ?? []), t]);
    return [...m];
  }, [d]);
  const open = commitments.filter((c) => c.status === 'planned' || c.status === 'committed');
  return (
    <Panel
      title="Сменные задания прорабам"
      meta={d?.mineOnly ? 'ваши задания' : 'объём на день из плана недели'}
    >
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorBox error={q.error} /> : null}
      {d?.canIssue && open.length ? (
        <IssueForm
          scheduleId={scheduleId}
          week={week}
          commitments={open}
          people={people}
          onIssued={refresh}
        />
      ) : null}
      {d ? (
        byDay.length ? (
          <div className="mt-3 flex flex-col gap-3">
            {byDay.map(([day, list]) => (
              <div key={day}>
                <h3 className="mb-1 text-[13px] font-semibold">{dd(day)}</h3>
                <ul className="divide-y divide-line text-[14px]">
                  {list.map((t) => (
                    <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                      <span className="min-w-0">
                        <span className="font-medium">
                          {t.code} {t.name}
                        </span>
                        <span className="block text-[12px] text-ink-2">
                          {t.foremanName ?? 'прораб'} · план {num(t.quantity)} {t.unit} ·{' '}
                          {SHIFT_METHODS[t.method].toLowerCase()}
                          {t.reported ? ` · принято ${num(t.fact)} ${t.unit}` : ''}
                          {t.exceptionReason ? ` · выдано с ограничениями: ${t.exceptionReason}` : ''}
                        </span>
                      </span>
                      <span className="flex flex-wrap items-center gap-1">
                        {t.status === 'cancelled' ? (
                          <Badge>отменено</Badge>
                        ) : t.reported ? (
                          <Badge tone="success">есть отчёт</Badge>
                        ) : (
                          <Badge tone="warning">выдано</Badge>
                        )}
                        {t.status === 'issued' ? (
                          <Link
                            to="/site"
                            search={{
                              project: projectId,
                              package: t.packageId,
                              day: t.day,
                              draft: undefined,
                              report: undefined,
                            }}
                            className="inline-flex min-h-11 items-center px-2 text-[13px] text-accent underline md:min-h-0"
                          >
                            Отчёт смены
                          </Link>
                        ) : null}
                        {t.canCancel ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={cancel.isPending}
                            onClick={() => cancel.mutate(t.id)}
                            className="max-md:h-11"
                          >
                            Отменить
                          </Button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-[14px] text-ink-2">
            {d.mineOnly
              ? 'На эту неделю заданий вам нет.'
              : 'На эту неделю сменных заданий нет. Выберите обязательство недели, день и прораба.'}
          </p>
        )
      ) : null}
    </Panel>
  );
}

function IssueForm({
  scheduleId,
  week,
  commitments,
  people,
  onIssued,
}: {
  scheduleId: string;
  week: string;
  commitments: CommitmentRow[];
  people: Array<{ id: string; name: string }>;
  onIssued: () => void;
}) {
  const [cid, setCid] = useState(commitments[0]?.id ?? '');
  const c = commitments.find((x) => x.id === cid) ?? commitments[0];
  const [day, setDay] = useState(c?.planStart ?? week);
  const [foreman, setForeman] = useState('');
  const [method, setMethod] = useState<'even' | 'curve' | 'manual'>('even');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [needReason, setNeedReason] = useState(false);
  useEffect(() => {
    if (c && (day < c.planStart || day > c.planFinish)) setDay(c.planStart);
  }, [c, day]);
  const hint = useQuery({
    queryKey: ['shift-hint', scheduleId, c?.activityId, day],
    enabled: Boolean(c),
    queryFn: () =>
      api<ShiftQuantityHint>(`/schedules/${scheduleId}/shift-tasks/hint`, {
        query: { activityId: c!.activityId, day },
      }),
  });
  const h = hint.data;
  const suggested = method === 'curve' ? h?.curve : method === 'even' ? h?.even : null;
  const m = useMutation({
    mutationFn: () =>
      api<ShiftTaskList>(`/schedules/${scheduleId}/shift-tasks`, {
        body: {
          day,
          foremanId: foreman,
          items: [
            method === 'manual'
              ? { activityId: c!.activityId, quantity: qty.replace(',', '.').trim() }
              : { activityId: c!.activityId, method },
          ],
          ...(needReason && reason.trim() ? { exceptionReason: reason.trim() } : {}),
        },
      }),
    onSuccess: () => {
      toast('Задание выдано: прораб видит его в «Моём дне»');
      setQty('');
      setReason('');
      setNeedReason(false);
      onIssued();
    },
    onError: (e: Error) => {
      if (/ограничен/.test(e.message)) setNeedReason(true);
      toast(e.message, 'danger');
    },
  });
  if (!c) return null;
  const ready =
    foreman &&
    h?.working &&
    (method === 'manual'
      ? /^\d+([.,]\d{1,6})?$/.test(qty.trim()) && Number(qty.replace(',', '.')) > 0
      : suggested);
  return (
    <div className="flex flex-col gap-2 rounded-md bg-surface-2 px-2 py-2">
      <div className="flex flex-wrap items-end gap-2">
        <LField label="Работа из плана недели">
          <Select
            value={c.id}
            onChange={(e) => setCid(e.target.value)}
            className="w-72 max-w-full max-md:h-11"
          >
            {commitments.map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} {x.name}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="День смены">
          <Input
            type="date"
            min={c.planStart}
            max={c.planFinish}
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="max-md:h-11"
          />
        </LField>
        <LField label="Прораб">
          <Select
            value={foreman}
            onChange={(e) => setForeman(e.target.value)}
            className="w-56 max-w-full max-md:h-11"
          >
            <option value="">Выберите</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </LField>
        <LField label="Объём на день">
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value as typeof method)}
            className="max-md:h-11"
          >
            {Object.entries(SHIFT_METHODS).map(([k, t]) => (
              <option key={k} value={k}>
                {t}
              </option>
            ))}
          </Select>
        </LField>
        {method === 'manual' ? (
          <LField label={`Объём${h?.unit ? `, ${h.unit}` : ''}`}>
            <Input
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder={h?.even ? num(h.even) : ''}
              className="w-28 max-md:h-11"
            />
          </LField>
        ) : null}
      </div>
      <p className="text-[12px] text-ink-2">
        {hint.isPending
          ? 'Считаю объём на день…'
          : h?.problem
            ? h.problem
            : h
              ? `Остаток ${num(h.remaining ?? '0')} ${h.unit ?? ''}: равномерно — ${num(h.even ?? '—')}, по кривой (${h.curveName.toLowerCase()}) — ${num(h.curve ?? '—')}`
              : ''}
      </p>
      {needReason ? (
        <LField label="Причина выдачи с открытыми ограничениями">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full max-w-md max-md:h-11"
          />
        </LField>
      ) : null}
      <div>
        <Button
          variant="primary"
          disabled={!ready || (needReason && reason.trim().length < 3)}
          loading={m.isPending}
          onClick={() => m.mutate()}
          className="max-md:h-11"
        >
          Выдать задание
          {method !== 'manual' && suggested ? ` · ${num(suggested)} ${h?.unit ?? ''}` : ''}
        </Button>
      </div>
    </div>
  );
}
